/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react/display-name */
import { List, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as Cesium from "cesium";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapAreaItemDto } from "~/api/GeneratedApi";
import { useAppSelector } from "~/redux/store/store";
import {
  CesiumCadItem,
  CesiumDrawerItem,
  CesiumImageryItem,
  CesiumMapItem,
  CesiumMeasurementItem,
  CesiumTerrainItem,
} from "../types/CesiumMapItems";
import { CesiumDrawerCadItem } from "./CesiumDrawerCadItem";
import CesiumDrawerDataItem from "./CesiumDrawerDataItem";
import { CesiumDrawerImageryItem } from "./CesiumDrawerImageryItem";
import { CesiumDrawerTerrainItem } from "./CesiumDrawerTerrainItem";
import { MapAreaAccordion } from "./MapAreaAccordion";

const useStyles = makeStyles((theme) => ({
  emptyText: {
    color: theme.palette.primary.contrastText,
  },
}));

type Props = {
  viewer: Cesium.Viewer;
};

export type DrawerItem<T extends CesiumMapItem> = {
  mapItem: T;
  entity: Cesium.Entity;
} & CesiumDrawerItem;

type GroupedType = {
  mapArea: MapAreaItemDto | null;
  items: DrawerItem<CesiumMapItem>[];
};
type GroupedState = { [key: number]: GroupedType };

const CesiumDrawerDataItems = React.forwardRef<HTMLUListElement, Props>(
  (props: Props, ref): JSX.Element | null => {
    const { viewer } = props;
    const [items, setItems] = useState<DrawerItem<CesiumMapItem>[]>();
    const [cadItemCount, setCadItemCount] = React.useState<number>(0);
    const [grouped, setGrouped] = React.useState<GroupedState>({});
    const classes = useStyles();
    const { t } = useTranslation("cesium");
    const hoveredItem = useAppSelector((s) => s.map.hoveredCesiumItem);
    const hoveredItemId = useMemo(
      () => hoveredItem?.mapId,
      [hoveredItem?.mapId],
    );
    const [terrainChanged, setTerrainChanged] = React.useState(false);

    const entitiesToState = useCallback(
      (collection: Cesium.EntityCollection) => {
        const itemArr: DrawerItem<CesiumMapItem>[] = [];
        collection.values.forEach((e) => {
          const properties = e.properties?.getValue(Cesium.JulianDate.now());
          if (properties && properties?.type === "measurement") {
            const itemProps = properties as CesiumMeasurementItem;
            itemArr.push({
              mapItem: itemProps,
              entity: e,
              mapAreas: itemProps.mapAreas ?? [],
            });
          } else if (properties && properties.type === "imagery") {
            const itemProps = properties as CesiumImageryItem;
            itemArr.push({
              mapItem: itemProps,
              entity: e,
              mapAreas: itemProps.mapArea ? [itemProps.mapArea] : [],
            });
          } else if (properties && properties.type === "terrain") {
            const itemProps = properties as CesiumTerrainItem;
            itemArr.push({
              mapItem: itemProps,
              entity: e,
              mapAreas: itemProps.mapArea ? [itemProps.mapArea] : [],
            });
          } else if (properties && properties.type === "cad") {
            const itemProps = properties as CesiumCadItem;
            itemArr.push({
              mapItem: itemProps,
              entity: e,
              mapAreas: itemProps.mapArea ? [itemProps.mapArea] : [],
            });
          }
        });
        setItems(itemArr);
      },
      [],
    );

    // Check the map for entities
    useEffect(() => {
      const listener = (collection: Cesium.EntityCollection) => {
        entitiesToState(collection);
      };
      const removeListener =
        viewer.entities.collectionChanged.addEventListener(listener);
      entitiesToState(viewer.entities);
      return () => {
        removeListener();
      };
    }, [viewer.entities, entitiesToState]);

    const handleCadItemAdded = React.useCallback(
      () => setCadItemCount((prev) => prev + 1),
      [],
    );

    // Group items by mapArea
    React.useEffect(() => {
      if (!items) return;
      const grp = items.reduce<{
        [key: number]: GroupedType;
      }>((total, curr) => {
        return {
          ...total,
          ...(curr.mapAreas.length
            ? curr.mapAreas.reduce((tot, c) => {
                return {
                  ...tot,
                  [c.mapAreaId]: {
                    mapArea: c,
                    items: [curr, ...(total[c.mapAreaId]?.items ?? [])],
                  },
                };
              }, {})
            : ({
                [-1]: {
                  mapArea: null,
                  items: [curr, ...(total[-1]?.items ?? [])],
                },
              } as {
                [key: number]: GroupedType;
              })),
        };
      }, {});
      setGrouped(grp);
    }, [items]);

    const renderItems = React.useCallback(() => {
      return Object.keys(grouped).map((k) => {
        const el = grouped[k as unknown as keyof GroupedState];
        const mapAreaName = el.mapArea?.name;
        return (
          <MapAreaAccordion
            title={mapAreaName ?? t("no-map-area")}
            key={el.mapArea?.mapAreaId ?? -1}
          >
            {el.items?.map((m) => {
              if (m.mapItem.type === "measurement") {
                return (
                  <CesiumDrawerDataItem
                    key={m.mapItem.mapId}
                    item={m as DrawerItem<CesiumMeasurementItem>}
                    viewer={viewer}
                    hovered={hoveredItemId === m.mapItem.mapId}
                  />
                );
              }
              if (m.mapItem.type === "imagery") {
                return (
                  <CesiumDrawerImageryItem
                    key={m.mapItem.mapId}
                    item={m as DrawerItem<CesiumImageryItem>}
                    viewer={viewer}
                  />
                );
              }
              if (m.mapItem.type === "terrain") {
                return (
                  <CesiumDrawerTerrainItem
                    key={m.mapItem.mapId}
                    item={m as DrawerItem<CesiumTerrainItem>}
                    viewer={viewer}
                    terrainChanged={terrainChanged}
                    onTerrainChange={() => setTerrainChanged((x) => !x)}
                  />
                );
              }
              if (m.mapItem.type === "cad") {
                return (
                  <CesiumDrawerCadItem
                    key={m.mapItem.mapId}
                    item={m as DrawerItem<CesiumCadItem>}
                    itemCount={cadItemCount}
                    itemAdded={handleCadItemAdded}
                    viewer={viewer}
                  />
                );
              }
              return null;
            })}
          </MapAreaAccordion>
        );
      });
    }, [
      cadItemCount,
      grouped,
      handleCadItemAdded,
      hoveredItemId,
      t,
      terrainChanged,
      viewer,
    ]);

    if (!items?.length) {
      return (
        <Typography
          variant="h6"
          align="center"
          className={classes.emptyText}
          ref={ref}
        >
          {t("no-measurements")}
        </Typography>
      );
    }
    return (
      <List ref={ref} sx={{ height: "100%", overflow: "auto" }}>
        {renderItems()}
      </List>
    );
  },
);

export default CesiumDrawerDataItems;
